import { Terminal, type ILink } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import type { TerminalHandle as VmTerminalHandle, Vm } from '@engineer-os/vm';
import { api } from './api';
import { findLocations } from './lib/run';
import { getStrings, type Strings } from './lib/strings';
import { tango } from './lib/tango';
import { bufferTail } from './lib/terminal-tail';
import type { GitState } from './types';

/**
 * Browser terminal bound to the learner's shell on the VM's first serial line.
 *
 * The shape of this class is unchanged from the WebSocket version — the host
 * component still calls `connect`/`reconnect`/`dispose` and reads status text
 * out of `onStatus` — but everything behind it is now in this tab. Output and
 * input travel over ttyS0, and `onState` is driven by ttyS2: the guest's
 * PROMPT_COMMAND emits one byte each time an interactive command finishes, so
 * the graph refreshes exactly once per command instead of on the two timers
 * the server used to guess with.
 */

/**
 * Where the VM comes from.
 *
 * The terminal must not boot or own the VM: the app boots it once behind the
 * loading screen and every consumer — this class, the file API, the grader —
 * shares that one instance. So whoever boots it registers it here, and this
 * class asks for it at connect time. The provider is async because a `connect`
 * can land while the boot is still running.
 */
export type VmProvider = () => Vm | Promise<Vm>;

let vmProvider: VmProvider | null = null;

/** Called once by the app, as soon as the VM exists. */
export function setVmProvider(provider: VmProvider): void {
  vmProvider = provider;
}

/**
 * What makes a `path:line` in the output clickable (lib/run.ts finds them).
 * `resolve` says whether a printed path is one of the learner's files and
 * which, as the editor names it; `open` is the click.
 */
export interface TerminalLocations {
  resolve(path: string): string | null;
  open(path: string, line: number, col: number | null): void;
}

/* The two sentences in the next two functions, and only these two, stay
 * English in both languages: they are written into the xterm buffer by
 * `attach`, which is a `dir="ltr"` character grid holding real shell output a
 * line above and a line below. Arabic there is reordered by bidi into
 * nonsense, and a
 * learner cannot tell a mangled message from a mangled command (docs/i18n.md
 * §7). The status words the *pane head* shows are prose and are translated;
 * they come from the catalogue, below.
 */
async function resolveVm(): Promise<Vm> {
  if (!vmProvider) throw new Error('the machine is not running yet');
  return vmProvider();
}

function describe(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'the machine could not be reached';
}

/**
 * The catalogue, read at the moment the status changes.
 *
 * `getStrings()` rather than `useStrings()`: this is a class that owns an
 * xterm instance, not a component, and there is nothing here for a hook to
 * re-render. The cost is that a status already pushed does not re-translate
 * when the language changes — `connected` sits there in the old language
 * until the next connect — which is the right trade for one word that is
 * replaced by the next thing the session does.
 */
function status(): Strings['boot']['terminal'] {
  return getStrings().boot.terminal;
}

export class SessionTerminal {
  readonly term: Terminal;
  private fit = new FitAddon();
  private attachment: VmTerminalHandle | null = null;
  private unsubscribeDone: (() => void) | null = null;
  private unsubscribeDead: (() => void) | null = null;
  private sessionId: string | null = null;
  private disposed = false;
  private ro: ResizeObserver | null = null;
  /**
   * Bumped by every `connect`/`disconnect`. Attaching is asynchronous (the VM
   * may still be booting), so a reply that arrives after a reconnect has to
   * recognise that it belongs to an abandoned attempt and do nothing.
   */
  private generation = 0;
  /** One git state read in flight at a time; a byte that arrives during one
   * collapses into a single follow-up rather than a queue of stale reads. */
  private reading = false;
  private readPending = false;
  /**
   * Whether the shell is sitting at its prompt, which is the only moment a
   * line typed for the learner (`type`) lands where they would expect it.
   *
   * Set by the command-done byte, which PROMPT_COMMAND sends just before the
   * prompt is drawn, and cleared by any Enter. So it is false while anything
   * the learner started is still running — a server in the foreground, a
   * `node` REPL, `nano`, `less`, a `for` loop waiting at its `>` — and it
   * comes back on its own when that thing exits and bash prompts again. It
   * starts false: until the shell has prompted once since attaching, nothing
   * is known about what it is doing.
   */
  private idle = false;
  /**
   * A line was entered since the last prompt: an Enter from the learner, or
   * `type` for them. The prompt bash redraws after `attach` writes its own
   * newline (and after a reset) is not a command anybody ran, and reporting
   * it as one told the stuck detector that a learner who had typed nothing
   * had already started (state/useStuck.ts, "never starting").
   */
  private entered = false;
  /** A size change waiting to be sent to the guest (`sendSize`). */
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  /** Held as a field so `dispose()` can actually unsubscribe it. */
  private readonly onWindowResize = (): void => this.refit();

  constructor(
    private readonly container: HTMLElement,
    private readonly onState: (state: GitState) => void,
    private readonly onStatus: (text: string) => void,
    /**
     * How the command that just finished ended: false when it exited
     * non-zero, undefined when the guest does not say. The host uses it to
     * tell working from flailing; nothing here depends on it, and an image
     * that never reports one simply never calls this with a value.
     */
    private readonly onCommandResult: (ok?: boolean) => void = () => {},
    /**
     * Where a `path:line` the shell printed leads, when it leads somewhere.
     * Without it nothing in the output is a link, as before.
     */
    locations?: TerminalLocations,
  ) {
    this.term = new Terminal({
      cursorBlink: true,
      fontFamily: 'ui-monospace, "Cascadia Code", "JetBrains Mono", "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
      fontSize: 13,
      lineHeight: 1.25,
      scrollback: 5000,
      allowProposedApi: true,
      // The ANSI ramp is Ubuntu's — the Tango palette GNOME Terminal has
      // shipped as the default profile for years. The point is transfer: what
      // a learner reads here is byte-for-byte what they will read on a real
      // box, so red means a deletion, green an addition, and the blue/cyan in
      // the prompt (see sandbox/bashrc) lands where muscle memory expects it.
      theme: {
        // Ubuntu's aubergine (#300a24) is swapped for the product's floor
        // black: the pane's own background is --paper, and two different
        // darks meeting at the padding edge reads as a bug, not a theme.
        background: '#000000',
        foreground: tango.brightWhite,
        // The cursor stays the brand's Signal Green rather than Ubuntu's
        // white. It is the one live thing in the pane and nothing in the
        // Tango ramp is near that hue, so it never reads as output.
        cursor: '#a3f01c',
        cursorAccent: '#000000',
        selectionBackground: '#b5d5ff',
        selectionForeground: '#000000',
        // The sixteen live in lib/tango.ts, because the code editor in the
        // next tab now syntax-highlights against the same ramp.
        ...tango,
      },
    });
    this.term.loadAddon(this.fit);
    this.term.open(container);
    this.fit.fit();

    // Keystrokes and geometry are dropped while detached rather than buffered:
    // there is no shell listening, and the respawned one starts fresh anyway.
    this.term.onData((data) => this.send(data));
    this.term.onResize(({ cols, rows }) => this.sendSize(cols, rows));

    this.ro = new ResizeObserver(() => this.refit());
    this.ro.observe(container);
    window.addEventListener('resize', this.onWindowResize);

    // Disposed with the terminal, like every provider xterm holds.
    if (locations) this.term.registerLinkProvider({ provideLinks: (y, done) => done(this.linksAt(y, locations)) });
  }

  /**
   * The places in the learner's code on one line of output, as links.
   *
   * xterm asks per row, but a stack frame is routinely longer than the
   * terminal is wide — `(file:///workspace/storefront/src/report.js:8:6)` is
   * forty-odd columns before the indent — so the row is widened to the whole
   * logical line it belongs to, matched as one string, and each match mapped
   * back to the cells it is drawn in. The mapping is cell by cell rather than
   * `offset % cols` so that a wide character earlier on the line cannot shift
   * every link after it.
   */
  private linksAt(y: number, locations: TerminalLocations): ILink[] | undefined {
    const buffer = this.term.buffer.active;
    let first = y - 1;
    while (first > 0 && buffer.getLine(first)?.isWrapped) first -= 1;
    let last = y - 1;
    while (buffer.getLine(last + 1)?.isWrapped) last += 1;

    let text = '';
    const cells: { x: number; y: number }[] = [];
    for (let row = first; row <= last; row += 1) {
      const line = buffer.getLine(row);
      if (!line) break;
      for (let x = 0; x < line.length; x += 1) {
        const cell = line.getCell(x);
        // The second half of a wide character has width 0 and no text.
        if (!cell || cell.getWidth() === 0) continue;
        const chars = cell.getChars() || ' ';
        for (let i = 0; i < chars.length; i += 1) {
          text += chars[i];
          cells.push({ x: x + 1, y: row + 1 });
        }
      }
    }

    const links: ILink[] = [];
    for (const found of findLocations(text)) {
      const path = locations.resolve(found.path);
      const start = cells[found.start];
      const end = cells[found.end - 1];
      if (path === null || !start || !end) continue;
      links.push({
        range: { start, end },
        text: text.slice(found.start, found.end),
        activate: () => locations.open(path, found.line, found.col),
      });
    }
    return links.length > 0 ? links : undefined;
  }

  /**
   * Re-measure and refit. Public because a host that reveals the terminal
   * after it was hidden (a tab switch) has to ask for a fit once the element
   * has a box again; xterm cannot measure a `display:none` container.
   */
  refit(): void {
    if (this.disposed) return;
    try {
      this.fit.fit();
    } catch {
      /* container not visible yet */
    }
  }

  connect(sessionId: string): void {
    if (this.disposed) return;
    this.disconnect();
    this.sessionId = sessionId;
    this.refit();
    this.onStatus(status().connecting);
    void this.attach(this.generation);
  }

  private async attach(generation: number): Promise<void> {
    try {
      const vm = await resolveVm();
      // The only definition of "up" that matters: the guest agent answered.
      await vm.ready();
      if (this.disposed || this.generation !== generation) return;

      this.attachment = vm.attachTerminal({ onData: (data) => this.term.write(data) });
      this.unsubscribeDone = vm.onCommandDone((ok) => {
        this.idle = true;
        this.noteCommand();
        if (this.entered) {
          this.entered = false;
          this.onCommandResult(ok);
        }
        this.readState();
      });
      this.unsubscribeDead = vm.onDead((error) => {
        if (this.disposed || this.generation !== generation) return;
        this.term.write(`\r\n\x1b[97m[${describe(error)}]\x1b[0m\r\n`);
        this.disconnect();
        this.onStatus(status().disconnected);
      });
      this.onStatus(status().connected);
      this.attachment.resize(this.term.cols, this.term.rows);
      // Everything ttyS0 printed before this moment is gone — the prompt after
      // a state restore, or the one the respawned shell drew during a reset.
      // A bare newline makes bash draw it again into a screen we can see.
      this.attachment.write('\n');
      this.term.focus();
      this.readState();
    } catch (err) {
      if (this.disposed || this.generation !== generation) return;
      this.term.write(`\r\n\x1b[97m[${describe(err)}]\x1b[0m\r\n`);
      this.onStatus(status().disconnected);
    }
  }

  /**
   * Read the git state and hand it to the host. Called once per finished
   * command, so it coalesces: a command that finishes while a read is in
   * flight schedules exactly one more.
   */
  private readState(): void {
    const sessionId = this.sessionId;
    if (!sessionId) return;
    if (this.reading) {
      this.readPending = true;
      return;
    }
    this.reading = true;
    const generation = this.generation;
    void api
      .getState(sessionId)
      .then((state) => {
        if (!this.disposed && this.generation === generation) this.onState(state);
      })
      .catch(() => {
        /* a transient read failure just means the graph waits for the next command */
      })
      .finally(() => {
        this.reading = false;
        if (this.readPending) {
          this.readPending = false;
          this.readState();
        }
      });
  }

  /**
   * Count the command for the session. The same byte drives the graph, and
   * it is also the only trace the tab has of work at the prompt (the bytes
   * on ttyS0 are the learner's keystrokes, not parsed commands). It fires
   * once for the prompt bash redraws after `attach` writes its newline, so
   * the count can run one high; that errs towards believing the learner,
   * which is the side to err on.
   */
  private noteCommand(): void {
    const sessionId = this.sessionId;
    if (!sessionId) return;
    void api.commandDone(sessionId).catch(() => {
      /* a session that is gone has nothing to count */
    });
  }

  /**
   * Tell the guest the new size, once the size has stopped changing.
   *
   * Every resize is an `stty` on the command channel, queued behind whatever
   * else the channel is doing — the git state read, a save, a Check. Dragging
   * a divider changes the column count dozens of times a second, and sending
   * each one would queue seconds of `stty` in front of the next command. Only
   * the size the drag ends on matters, so that is the one sent.
   */
  private sendSize(cols: number, rows: number): void {
    if (this.resizeTimer !== null) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.resizeTimer = null;
      this.attachment?.resize(cols, rows);
    }, 150);
  }

  /**
   * Bytes as if they had been typed: every keystroke comes through here, and
   * so do the phone's key bar's (components/play/PhoneKeys.tsx), which is how
   * a Tab or a Ctrl-C from a button counts exactly as one from a keyboard.
   */
  send(data: string): void {
    if (this.disposed) return;
    // Enter, or a paste with a line in it: whatever was at the prompt is
    // running now.
    if (/[\r\n]/.test(data)) {
      this.idle = false;
      this.entered = true;
    }
    this.attachment?.write(data);
  }

  /**
   * Type a line at the prompt and press Enter, exactly as the learner would:
   * bash echoes it, it goes into their history, and its output lands in their
   * scrollback. This is how the editor's Run button runs anything, so that
   * the command is something they have seen and can type themselves.
   *
   * Refused while the shell is not at its prompt, because the line would go
   * to whatever is running instead (see `idle`). Anything half typed at the
   * prompt is cut first — Ctrl+E, then Ctrl+U, which puts it on the kill ring
   * where Ctrl+Y brings it back — so the command is never appended to it.
   */
  type(line: string): 'typed' | 'busy' | 'detached' {
    if (this.disposed || !this.attachment) return 'detached';
    if (!this.idle) return 'busy';
    this.idle = false;
    this.entered = true;
    this.attachment.write(`\x05\x15${line}\r`);
    return 'typed';
  }

  /**
   * The line the cursor is on, whole even where xterm wrapped it across rows.
   * At an idle shell that is the prompt, plus anything half typed after it,
   * which is how the Run button knows where the shell is standing without
   * asking the guest (lib/run.ts, `promptDirectory`).
   */
  cursorLine(): string {
    if (this.disposed) return '';
    const buffer = this.term.buffer.active;
    let y = buffer.baseY + buffer.cursorY;
    let text = buffer.getLine(y)?.translateToString(true) ?? '';
    // A wrapped row's predecessor is full width, trailing spaces included.
    while (y > 0 && buffer.getLine(y)?.isWrapped) {
      y -= 1;
      text = (buffer.getLine(y)?.translateToString(false) ?? '') + text;
    }
    return text;
  }

  requestState(): void {
    this.readState();
  }

  clear(): void {
    this.term.reset();
  }

  disconnect(): void {
    this.generation += 1;
    this.idle = false;
    this.entered = false;
    if (this.resizeTimer !== null) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
    if (this.unsubscribeDone) {
      const off = this.unsubscribeDone;
      this.unsubscribeDone = null;
      off();
    }
    if (this.unsubscribeDead) {
      const off = this.unsubscribeDead;
      this.unsubscribeDead = null;
      off();
    }
    if (this.attachment) {
      const handle = this.attachment;
      this.attachment = null;
      handle.detach();
    }
  }

  /**
   * Used after a mission reset. The seeder killed the shell on ttyS0 and
   * inittab respawned it in the new working directory, so reconnecting means
   * clearing the old level's scrollback and attaching to that new shell.
   */
  reconnect(): void {
    if (this.disposed) return;
    if (this.sessionId) {
      this.clear();
      this.connect(this.sessionId);
    }
  }

  focus(): void {
    if (this.disposed) return;
    this.term.focus();
  }

  /**
   * The newest part of what is on this terminal, as plain text: the lines the
   * mentor is shown (lib/terminal-tail.ts). The active buffer, so a learner
   * with `nano` open is described by what `nano` is showing them.
   */
  recentText(): string {
    if (this.disposed) return '';
    return bufferTail(this.term.buffer.active);
  }

  /**
   * Tear the whole thing down: the attachment, the observers, and the xterm
   * instance itself. React 19's strict mode mounts every effect twice in
   * development, so without this the second mount would stack a second
   * <canvas> on top of the first and the learner would type into a ghost.
   * The guest's shell is left running — it is the session, not the view.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.disconnect();
    window.removeEventListener('resize', this.onWindowResize);
    this.ro?.disconnect();
    this.ro = null;
    try {
      this.term.dispose();
    } catch {
      /* already gone */
    }
  }
}
